/**
 * Field rules, mirrored from `server/src/utils/validate.js`.
 *
 * Why a copy rather than a shared package? Because the two halves deploy
 * separately, and a shared module would drag a build step into both. The
 * server stays the authority: everything here runs again there, and the API's
 * `details` object overwrites whatever this file decided. If the two ever
 * disagree, the server wins and the user sees the server's message.
 */

export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i;
export const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
export const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
export const SAFE_NAME_RE = /^[\p{L}\p{N}][\p{L}\p{N}\s.\-_/'"()&+]*$/u;

const str = (v) => (v === undefined || v === null ? '' : String(v).trim());

export function todayStr() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "+91 98290 11223" -> "9829011223", or null when it cannot be a valid number. */
export function normalisePhone(value) {
  let digits = str(value).replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
  else if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  return /^[6-9]\d{9}$/.test(digits) ? digits : null;
}

/** Returns an error message, or '' when the value is fine. */
export function checkField(field, spec, raw, values) {
  const name = spec.label || field;

  if (spec.type === 'array') {
    const arr = Array.isArray(raw) ? raw.filter(Boolean) : [];
    if (spec.required && arr.length === 0) return `Select at least one ${name.toLowerCase()}`;
    if (spec.oneOf) {
      const unknown = arr.filter((item) => !spec.oneOf.includes(item));
      if (unknown.length) return `Unknown ${name.toLowerCase()}: ${unknown.join(', ')}`;
    }
    return '';
  }

  if (spec.type === 'number' || spec.type === 'integer') {
    const empty = raw === '' || raw === null || raw === undefined;
    if (empty) return spec.required ? `${name} is required` : '';
    const n = Number(raw);
    if (!Number.isFinite(n)) return `${name} must be a number`;
    if (spec.type === 'integer' && !Number.isInteger(n)) return `${name} must be a whole number`;
    if (spec.min !== undefined && n < spec.min) {
      return spec.min === 1 ? `${name} must be at least 1` : `${name} must be ${spec.min} or more`;
    }
    if (spec.max !== undefined && n > spec.max) return `${name} cannot be more than ${spec.max}`;
    if (spec.moreThan !== undefined && n <= spec.moreThan) return `${name} must be greater than ${spec.moreThan}`;
    return '';
  }

  const value = str(raw);
  if (value === '') return spec.required ? `${name} is required` : '';

  if (spec.min && value.length < spec.min) return `${name} must be at least ${spec.min} characters`;
  if (spec.max && value.length > spec.max) return `${name} cannot be longer than ${spec.max} characters`;
  if (spec.oneOf && !spec.oneOf.includes(value)) return `${name} must be one of: ${spec.oneOf.join(', ')}`;
  if (spec.safeName && !SAFE_NAME_RE.test(value)) {
    return `${name} must start with a letter or number and use only letters, numbers and . - _ / ' " ( ) & +`;
  }
  if (spec.email && !EMAIL_RE.test(value)) return 'Enter a valid email address, like name@example.com';
  if (spec.gstin && !GSTIN_RE.test(value.toUpperCase())) return 'Enter a valid 15-character GSTIN, like 08AAACA1234A1Z5';
  if (spec.phone && !normalisePhone(value)) return 'Enter a valid 10-digit mobile number starting with 6, 7, 8 or 9';
  if (spec.date) {
    if (!DATE_RE.test(value) || Number.isNaN(new Date(`${value}T00:00:00`).getTime())) {
      return `${name} must be a valid date (YYYY-MM-DD)`;
    }
    if (spec.notFuture && value > todayStr()) return `${name} cannot be in the future`;
  }
  if (spec.password) {
    if (!/[A-Za-z]/.test(value) || !/\d/.test(value)) {
      return `${name} must contain at least one letter and one number`;
    }
    if (spec.worstList?.includes(value.toLowerCase())) {
      return 'That password is too common. Please choose another.';
    }
  }
  if (spec.match && values && value !== values[spec.match]) {
    return spec.matchMessage || 'The two passwords do not match';
  }
  return '';
}

/** Validate a whole form. Returns { field: message } — empty when valid. */
export function validateForm(values, schema) {
  const errors = {};
  for (const [field, spec] of Object.entries(schema)) {
    const message = checkField(field, spec, values[field], values);
    if (message) errors[field] = message;
  }
  return errors;
}

/**
 * Validate entry-table rows.
 *
 * Two different index spaces are in play, and confusing them puts the red
 * border on the wrong row:
 *   - the *display* index, the row's position in the table (blanks included)
 *   - the *payload* index, its position in the array we POST (blanks dropped)
 *
 * `errors` is keyed by display index, because that is what the table renders.
 * `keep` maps payload index -> display index, so the server's errors — which
 * are keyed by payload index — can be translated back.
 */
export function validateRowSet(rows, schema, { key = 'rows', skipEmpty = true } = {}) {
  const errors = {};
  const payload = [];
  const keep = [];

  rows.forEach((row, displayIndex) => {
    const isBlank = Object.keys(schema).every((field) => {
      const v = row[field];
      return v === '' || v === undefined || v === null;
    });
    if (isBlank && skipEmpty) return;

    payload.push(row);
    keep.push(displayIndex);

    for (const [field, spec] of Object.entries(schema)) {
      const message = checkField(field, spec, row[field]);
      if (message) errors[`${key}.${displayIndex}.${field}`] = message;
    }
  });

  return { errors, payload, keep };
}

/**
 * Translate the server's row errors (`rows.0.qty`, keyed by payload index) into
 * the display indexes the table actually uses.
 */
export function remapRowErrors(details, key, keep) {
  const out = {};
  const prefix = `${key}.`;
  for (const [field, message] of Object.entries(details || {})) {
    if (!field.startsWith(prefix)) continue;
    const rest = field.slice(prefix.length);
    const dot = rest.indexOf('.');
    if (dot === -1) continue;

    const payloadIndex = Number(rest.slice(0, dot));
    if (!Number.isInteger(payloadIndex)) continue;
    const displayIndex = keep[payloadIndex];
    if (displayIndex === undefined) continue;

    out[`${key}.${displayIndex}.${rest.slice(dot + 1)}`] = message;
  }
  return out;
}

/** Look up the error for one cell of an entry table. */
export function rowError(errors, key, index, field) {
  return errors?.[`${key}.${index}.${field}`] || '';
}

export function hasErrors(errors) {
  return Boolean(errors) && Object.keys(errors).length > 0;
}
